import { GetterTree } from "vuex";
import { RootState } from "@/store/state";
import { SystemMode } from "@/models/system-mode";
import { ActiveView } from "@/models/active-view";
import { ISlide } from "@/models/slide";
import { WorkflowState } from "@/models/workflow-state";
import { RegulationAuthority } from "@/models/regulaton-authority";
import { ICase } from "@/models/case";
import { Likelihood } from "@/models/labels";

export enum RootGetters {
  // System Mode
  GET_SYSTEM_MODE = "GET_SYSTEM_MODE",
  IS_FIRST_READ = "IS_FIRST_READ",
  IS_SECOND_READ = "IS_SECOND_READ",

  // Routing
  GET_IS_STATISTICS_SCREEN = "GET_IS_STATISTICS_SCREEN",
  IS_CASE_REPORT_DISPLAYED = "IS_CASE_REPORT_DISPLAYED",
  GET_IS_DIRECT_LINK = "GET_IS_DIRECT_LINK",
  GET_ROUTE_PARAMS = "GET_ROUTE_PARAMS",

  // Config
  CONFIG = "CONFIG",
  LABELS_CONFIG = "LABELS_CONFIG",
  GET_HEATMAPS_CONFIG = "GET_HEATMAPS_CONFIG",

  // Slides
  ACTIVE_SLIDE = "ACTIVE_SLIDE",
  GET_SLIDE_BY_ID = "GET_SLIDE_BY_ID",
  GET_IS_SLIDE_COMPLETED = "GET_IS_SLIDE_COMPLETED",
  SLIDES = "SLIDES",
  GET_CURRENT_SLIDE = "GET_CURRENT_SLIDE",
  GET_SLIDE_LABELS = "GET_SLIDE_LABELS",
  GET_SLIDE_INSIGHTS = "GET_SLIDE_INSIGHTS",
  SLIDE_POIS = "SLIDE_POIS",

  // Cases
  GET_CASE_BY_ID = "GET_CASE_BY_ID",
  GET_ACTIVE_CASE = "GET_ACTIVE_CASE",
  HAS_ACTIVE_CASE = "HAS_ACTIVE_CASE",
  GET_ACTIVE_CASE_TISSUE = "GET_ACTIVE_CASE_TISSUE",
  GET_IS_CASE_COMPLETION_ENABLED = "GET_IS_CASE_COMPLETION_ENABLED",
  GET_IS_CASE_COMPLETE = "GET_IS_CASE_COMPLETE",
  IS_CASE_HAS_TECHNICAL_ISSUES = "IS_CASE_HAS_TECHNICAL_ISSUES",
  INCOMPLETE_CASES_COUNT = "INCOMPLETE_CASES_COUNT",

  // Auth
  GET_REGULATORY_AUTHORITY = "GET_REGULATORY_AUTHORITY",

  // Globals
  GET_GLOBALS = "GET_GLOBALS",

  // Query Params
  GET_QUERY_PARAMS = "GET_QUERY_PARAMS",

  // Labeling

  GET_LABELING = "GET_LABALING",
}

const getters: GetterTree<RootState, RootState> = {
  // System Mode

  [RootGetters.GET_SYSTEM_MODE](state: RootState): string {
    return state.systemMode;
  },
  [RootGetters.IS_FIRST_READ](state: RootState): boolean {
    return state.systemMode === SystemMode.firstread;
  },
  [RootGetters.IS_SECOND_READ](state: RootState, getters: any): boolean {
    return !getters.IS_FIRST_READ;
  },

  // Routing

  [RootGetters.GET_IS_STATISTICS_SCREEN](state: RootState): boolean {
    return state.activeView === ActiveView.Statistics;
  },

  [RootGetters.IS_CASE_REPORT_DISPLAYED](state: RootState): boolean {
    return state.activeView === ActiveView.CaseReport;
  },

  [RootGetters.GET_IS_DIRECT_LINK](state: RootState): boolean {
    if (!!state.queryParams?.get("case")) {
      return true;
    }
    if (state.routeParams?.name === "cl") {
      return true;
    }
    return false;
  },

  [RootGetters.GET_ROUTE_PARAMS](state: RootState): any {
    return state.routeParams;
  },

  // Config
  [RootGetters.CONFIG](state: RootState): any {
    return state.config;
  },

  [RootGetters.LABELS_CONFIG](state: RootState): any {
    return state.insightsConfig?.labels;
  },

  [RootGetters.GET_HEATMAPS_CONFIG](state: RootState): any {
    return state.heatmapsConfig?.[state.currentSlide.tissueType]?.[state.currentSlide.stain] || [];
  },

  // Slides

  [RootGetters.ACTIVE_SLIDE](state: RootState, getters: any): ISlide | undefined {
    return getters.GET_SLIDE_BY_ID(state.activeSlideId);
  },

  [RootGetters.GET_SLIDE_BY_ID](state: RootState): Function {
    return (slideId: string): ISlide | undefined => {
      return state.slides.find((slide) => slide.slideId === slideId);
    };
  },

  [RootGetters.SLIDES](state: RootState): ISlide[] {
    return state.slides;
  },

  [RootGetters.GET_IS_SLIDE_COMPLETED](): Function {
    return (slide: ISlide): boolean => {
      return slide.workflowState === WorkflowState.COMPLETE;
    };
  },

  [RootGetters.GET_CURRENT_SLIDE](state: RootState): ISlide {
    return state.currentSlide;
  },

  [RootGetters.GET_SLIDE_LABELS](state: RootState, getters: any): Function {
    return (slideId: string) => {
      const currentCase = getters.GET_ACTIVE_CASE;
      return currentCase.caseLabels?.slides[slideId]?.labels?.filter(
        (label) => label.likelihood !== Likelihood.LOW
      );
    };
  },
  [RootGetters.GET_SLIDE_INSIGHTS](state: RootState, getters: any): Array<any> {
    return getters.ACTIVE_SLIDE.insights || [];
  },

  [RootGetters.SLIDE_POIS](state: RootState): object[] {
    return state.currentSlide.POIs;
  },

  // Cases

  [RootGetters.GET_CASE_BY_ID](state: RootState): Function {
    return (caseId: string): ICase | undefined => {
      return state.cases.find((caseObj) => caseObj.caseId === caseId);
    };
  },

  [RootGetters.HAS_ACTIVE_CASE](state: RootState): boolean {
    return !!state.activeCaseId;
  },

  [RootGetters.GET_ACTIVE_CASE](state: RootState, getters: any): ICase | undefined {
    return getters.GET_CASE_BY_ID(state.activeCaseId);
  },

  [RootGetters.GET_ACTIVE_CASE_TISSUE](state: RootState, getters: any): ICase | undefined {
    return getters.GET_ACTIVE_CASE?.tissue;
  },

  [RootGetters.GET_IS_CASE_COMPLETION_ENABLED](state: RootState, getters: any): boolean {
    return state.slides.every((slide: ISlide) => getters.GET_IS_SLIDE_COMPLETED(slide));
  },

  [RootGetters.GET_IS_CASE_COMPLETE](): Function {
    return (caseObj: ICase): boolean => {
      return caseObj.workflowState === WorkflowState.COMPLETE;
    };
  },
  [RootGetters.IS_CASE_HAS_TECHNICAL_ISSUES](): Function {
    return (caseObj: ICase): boolean => {
      return !!caseObj.screeningClasses?.find((screeningClass) => {
        return screeningClass.key === "outOfFocus" || screeningClass.key === "inadequateTissue";
      });
    };
  },

  [RootGetters.INCOMPLETE_CASES_COUNT](state: RootState): number {
    let incompleteCases = state.cases.filter((caseObj) => {
      return caseObj.workflowState !== WorkflowState.COMPLETE;
    });
    return incompleteCases.length;
  },

  // Auth

  [RootGetters.GET_REGULATORY_AUTHORITY](state: RootState): boolean {
    return state.config.regulatory_authority || RegulationAuthority.CE; // CE is default
  },

  // Globals

  [RootGetters.GET_GLOBALS](state: RootState): any {
    return state.globals;
  },

  // QueryParams
  [RootGetters.GET_QUERY_PARAMS](state: RootState): URLSearchParams {
    return state.queryParams || new URLSearchParams();
  },

  // Labeling
  [RootGetters.GET_LABELING](state: RootState): any {
    return state.labeling;
  },
};

export default getters;
